import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { ref, onValue } from 'firebase/database';
import { database } from '../firebase';
import AdminSettingsModal from './AdminSettingsModal';
import '../styles/AdminPanel.css';
import '../styles/AdminDesignSystem.css';

// Secciones de navegación del sub-sidebar
const SIDEBAR_SECTIONS = [
    {
        label: '👥 Usuarios y Accesos',
        items: [
            { id: 'licenses-consolidated', label: 'Licencias y Accesos', icon: '📋' },
            { id: 'devices', label: 'Dispositivos', icon: '📱' },
            { id: 'assign-profile', label: 'Asignar Perfil', icon: '👤' },
            { id: 'shadow', label: 'Modo Shadow', icon: '🕵️' },
        ]
    },
    {
        label: '📚 Gestión Contenidos',
        items: [
            { id: 'quizzes', label: 'Gestor de Quizzes', icon: '📝', route: '/dashboard/admin/quizz-management' },
            { id: 'tutorials-admin', label: 'Videotutoriales', icon: '🎥' },
            { id: 'ocr-scanner', label: 'Escáner OCR', icon: '📄' },
        ]
    },
    {
        label: '🎓 Academia Vortex',
        items: [
            { id: 'cursos-vortex', label: 'Gestor Cursos Vortex', icon: '🎓', route: '/dashboard/admin/cursos' },
            { id: 'lector-temas', label: 'Lector de Temas', icon: '📖', route: '/dashboard/admin/lector-temas' },
        ]
    },
    {
        label: '🔧 Ajustes y Técnica',
        items: [
            { id: 'notifications', label: 'Notificaciones', icon: '🔔' },
            
            { id: 'maintenance', label: 'Ajustes y Mantenimiento', icon: '🛠️', route: '/dashboard/admin/maintenance' },
            { id: 'versions', label: 'Tracking Versiones', icon: '📈' },
            { id: 'rtdb', label: 'RTDB Explorer', icon: '🗄️' },
            { id: 'health-check', label: 'Diagnóstico del Sistema', icon: '🛡️', route: '/dashboard/admin/health-check' },
        ]
    }
];

const AdminLayout = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // Determinar panel activo desde URL (?panel=xxx) y ruta actual
    const searchParams = new URLSearchParams(location.search);
    const activePanel = searchParams.get('panel');
    const currentPath = location.pathname;
    const isRootAdmin = currentPath === '/dashboard/admin';

    // Estado de personalización (persiste mientras el admin esté en cualquier sub-ruta)
    const [backgroundId, setBackgroundId] = useState(parseInt(localStorage.getItem('adminBgId')) || 13);
    const [textColor, setTextColor] = useState(localStorage.getItem('adminTextColor') || '#FFFFFF');
    const [btnColor, setBtnColor] = useState(localStorage.getItem('adminBtnColor') || '#2196F3');
    const [showSettings, setShowSettings] = useState(false);

    // Aplicar variables CSS globales
    useEffect(() => {
        document.documentElement.style.setProperty('--admin-text-custom', textColor);
        document.documentElement.style.setProperty('--admin-btn-custom', btnColor);
    }, [textColor, btnColor]);

    // Navegar a un item del sidebar
    const goTo = (item) => {
        if (item.route) {
            navigate(item.route);
        } else {
            // Panel inline en AdminPanel via query param
            navigate(`/dashboard/admin?panel=${item.id}`);
        }
    };

    // Determinar si un item está activo
    const isItemActive = (item) => {
        if (item.route) {
            return currentPath.startsWith(item.route);
        }
        // Para items inline: comparar con ?panel=xxx
        return isRootAdmin && activePanel === item.id;
    };

    const btnStyle = (active) => ({
        background: active ? 'rgba(33,150,243,0.15)' : 'transparent',
        border: 'none',
        borderLeft: active ? '3px solid var(--admin-btn-custom,#2196F3)' : '3px solid transparent',
        color: active ? '#00f2fe' : '#b5c8d8',
        padding: '8px 12px',
        borderRadius: '0 6px 6px 0',
        textAlign: 'left',
        cursor: 'pointer',
        fontSize: '0.82rem',
        transition: 'all 0.2s',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        width: '100%',
    });

    return (
        <div className={`admin-panel-root bg-style-${backgroundId}`} style={{ color: '#fff', fontFamily: "'Inter', sans-serif" }}>
            {/* Top Nav */}
            <div className="admin-top-nav">
                <div className="nav-left">
                    <button onClick={() => navigate('/dashboard')} className="nav-btn-alt">
                        👤 Panel Usuario
                    </button>
                    <h1>Panel Admin</h1>
                </div>
                <div className="nav-right">
                    <button className="settings-trigger" onClick={() => setShowSettings(true)}>
                        ⚙️ Configuración
                    </button>
                </div>
            </div>

            {/* Layout split */}
            <div className="admin-layout-container">
                {/* Sub-sidebar persistente */}
                <div className="admin-sub-sidebar">
                    {/* Dashboard Principal */}
                    <button
                        onClick={() => navigate('/dashboard/admin')}
                        style={{
                            background: isRootAdmin && !activePanel
                                ? 'var(--admin-btn-custom,#2196F3)'
                                : 'rgba(255,255,255,0.03)',
                            border: 'none',
                            color: '#fff',
                            padding: '12px 16px',
                            borderRadius: '8px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            fontWeight: '600',
                            fontSize: '0.88rem',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            width: '100%',
                            marginBottom: '8px',
                        }}
                    >
                        <span>⚡</span> Dashboard Principal
                    </button>

                    {/* Secciones */}
                    {SIDEBAR_SECTIONS.map(section => (
                        <div key={section.label} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <span style={{
                                fontSize: '0.72rem',
                                color: '#c5a059',
                                textTransform: 'uppercase',
                                letterSpacing: '1px',
                                paddingLeft: '8px',
                                fontWeight: 'bold',
                                display: 'block',
                                marginTop: '8px',
                                marginBottom: '4px',
                            }}>
                                {section.label}
                            </span>
                            {section.items.map(item => {
                                const active = isItemActive(item);
                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => goTo(item)}
                                        style={btnStyle(active)}
                                        onMouseEnter={e => {
                                            if (!active) e.currentTarget.style.color = '#e2e8f0';
                                        }}
                                        onMouseLeave={e => {
                                            if (!active) e.currentTarget.style.color = '#b5c8d8';
                                        }}
                                    >
                                        <span>{item.icon}</span> {item.label}
                                    </button>
                                );
                            })}
                        </div>
                    ))}
                </div>

                {/* Contenido activo — Outlet renderiza la ruta hija correspondiente */}
                <div className="admin-main-content">
                    <Outlet />
                </div>
            </div>

            {showSettings && (
                <AdminSettingsModal
                    onClose={() => setShowSettings(false)}
                    textColor={textColor}
                    setTextColor={setTextColor}
                    btnColor={btnColor}
                    setBtnColor={setBtnColor}
                    backgroundId={backgroundId}
                    setBackgroundId={setBackgroundId}
                />
            )}
        </div>
    );
};

export default AdminLayout;
